import { useState, useMemo } from 'react';
import { Search, Plus, MapPin, Navigation, Pencil, Trash2, User, Calendar, Users, Store as StoreIcon, History, Globe, Filter, Route, RefreshCw, X, ChevronDown, ChevronUp, Building2, MapPinned } from 'lucide-react';
import { showToast } from './Toast';
import { NavigationModal } from './NavigationModal';

const LANGUAGE_LABELS = {
  'pt': 'Português',
  'en': 'Inglês',
  'es': 'Espanhol',
  'ar': 'Árabe',
  'zh': 'Chinês',
  'other': 'Outro'
};

const LANGUAGE_FLAGS = {
  'pt': '🇧🇷',
  'en': '🇺🇸',
  'es': '🇪🇸',
  'ar': '🇸🇦',
  'zh': '🇨🇳',
  'other': '🌐'
};

const LANGUAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos os Idiomas', flag: '🌍' },
  { value: 'pt', label: 'Português', flag: '🇧🇷' },
  { value: 'en', label: 'Inglês', flag: '🇺🇸' },
  { value: 'es', label: 'Espanhol', flag: '🇪🇸' },
  { value: 'ar', label: 'Árabe', flag: '🇸🇦' },
  { value: 'zh', label: 'Chinês', flag: '🇨🇳' },
  { value: 'other', label: 'Outro', flag: '🌐' }
];

export function LojasTab({ lojas, onEdit, onDelete, onGeocode, onPinMode, onViewOnMap, onRegeocodeAll }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [sellerFilter, setSellerFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [navigationLoja, setNavigationLoja] = useState(null);
  const [isRegeocoding, setIsRegeocoding] = useState(false);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const visitedLojas = lojas.filter(loja => !loja.status || loja.status === 'visitada');
    
    const cities = [...new Set(visitedLojas.map(l => l.city).filter(Boolean))].sort();
    const states = [...new Set(visitedLojas.map(l => l.state).filter(Boolean))].sort();
    const sellers = [];
    
    visitedLojas.forEach(loja => {
      (loja.sellers || []).forEach(seller => {
        if (seller.name && !sellers.find(s => s.name === seller.name)) {
          sellers.push({ name: seller.name, lojaId: loja.id, lojaName: loja.name });
        }
      });
    });
    
    return { cities, states, sellers: sellers.sort((a, b) => a.name.localeCompare(b.name)) };
  }, [lojas]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (cityFilter !== 'all') count++;
    if (stateFilter !== 'all') count++;
    if (sellerFilter !== 'all') count++;
    if (languageFilter !== 'all') count++;
    return count;
  }, [cityFilter, stateFilter, sellerFilter, languageFilter]);

  const clearAllFilters = () => {
    setCityFilter('all');
    setStateFilter('all');
    setSellerFilter('all');
    setLanguageFilter('all');
    setSearchTerm('');
  };

  const filteredLojas = useMemo(() => {
    // Filter only "visitada" status (or undefined for backwards compatibility)
    let result = lojas.filter(loja => !loja.status || loja.status === 'visitada');
    
    // Filter by language
    if (languageFilter !== 'all') {
      result = result.filter(loja => loja.language === languageFilter);
    }
    
    // Filter by city
    if (cityFilter !== 'all') {
      result = result.filter(loja => loja.city === cityFilter);
    }
    
    // Filter by state
    if (stateFilter !== 'all') {
      result = result.filter(loja => loja.state === stateFilter);
    }
    
    // Filter by seller
    if (sellerFilter !== 'all') {
      result = result.filter(loja => 
        (loja.sellers || []).some(s => s.name === sellerFilter)
      );
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(loja => 
        loja.name.toLowerCase().includes(term) ||
        loja.city.toLowerCase().includes(term) ||
        loja.state.toLowerCase().includes(term) ||
        loja.zip.includes(term) ||
        (loja.sellers || []).some(s => s.name?.toLowerCase().includes(term))
      );
    }
    
    return result;
  }, [lojas, searchTerm, languageFilter, cityFilter, stateFilter, sellerFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  const handleRegeocodeAll = async () => {
    if (!onRegeocodeAll) return;
    
    const lojasWithAddress = lojas.filter(l => l.address && l.city);
    if (lojasWithAddress.length === 0) {
      showToast('Nenhuma loja com endereço para atualizar', 'warning');
      return;
    }
    
    if (!window.confirm(`Atualizar GPS de ${lojasWithAddress.length} lojas usando Google Maps?\n\nIsso pode levar alguns segundos.`)) {
      return;
    }
    
    setIsRegeocoding(true);
    showToast(`Atualizando GPS de ${lojasWithAddress.length} lojas...`, 'info');
    
    try {
      await onRegeocodeAll();
      showToast('GPS de todas as lojas atualizado!', 'success');
    } catch (error) {
      showToast('Erro ao atualizar GPS', 'error');
    } finally {
      setIsRegeocoding(false);
    }
  };

  const handleDelete = (loja) => {
    if (window.confirm(`Excluir "${loja.name}"?`)) {
      onDelete(loja.id);
      showToast('Loja excluída com sucesso');
    }
  };

  const handleGeocode = async (loja) => {
    showToast('Buscando coordenadas...', 'info');
    await onGeocode(loja.id);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Search Bar */}
      <div className="sticky top-16 bg-[var(--bg-primary)] pt-4 pb-2 -mx-4 px-4 z-10 theme-transition">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            data-testid="input-search-lojas"
            type="text"
            placeholder="Buscar por nome, cidade, vendedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] transition-all placeholder:text-[var(--text-muted)]"
          />
        </div>
        
        {/* Filter Toggle Button */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFiltersCount > 0 
                ? 'bg-[var(--accent-primary)] text-white' 
                : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
            }`}
            data-testid="btn-toggle-filters"
          >
            <Filter size={16} />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="bg-white text-[var(--accent-primary)] text-xs font-bold px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
              data-testid="btn-clear-filters"
            >
              <X size={14} />
              Limpar
            </button>
          )}
          
          {/* Re-geocode All Button */}
          {onRegeocodeAll && (
            <button
              data-testid="btn-regeocode-all"
              onClick={handleRegeocodeAll}
              disabled={isRegeocoding}
              className="ml-auto flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 transition-colors"
              title="Atualizar GPS de todas as lojas"
            >
              <RefreshCw size={14} className={isRegeocoding ? 'animate-spin' : ''} />
              {isRegeocoding ? '...' : 'GPS'}
            </button>
          )}
        </div>

        {/* Expandable Filters Panel */}
        {showFilters && (
          <div className="mt-3 p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] space-y-4">
            {/* City Filter */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
                <Building2 size={14} />
                CIDADE
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCityFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    cityFilter === 'all'
                      ? 'bg-[var(--accent-primary)] text-white'
                      : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                  }`}
                >
                  Todas
                </button>
                {filterOptions.cities.map(city => (
                  <button
                    key={city}
                    onClick={() => setCityFilter(city)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      cityFilter === city
                        ? 'bg-[var(--accent-primary)] text-white'
                        : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>

            {/* State Filter */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
                <MapPinned size={14} />
                ESTADO
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setStateFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    stateFilter === 'all'
                      ? 'bg-[var(--accent-primary)] text-white'
                      : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                  }`}
                >
                  Todos
                </button>
                {filterOptions.states.map(state => (
                  <button
                    key={state}
                    onClick={() => setStateFilter(state)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      stateFilter === state
                        ? 'bg-[var(--accent-primary)] text-white'
                        : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            {/* Seller Filter */}
            {filterOptions.sellers.length > 0 && (
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
                  <User size={14} />
                  VENDEDOR
                </label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  <button
                    onClick={() => setSellerFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      sellerFilter === 'all'
                        ? 'bg-green-600 text-white'
                        : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                    }`}
                  >
                    Todos
                  </button>
                  {filterOptions.sellers.map(seller => (
                    <button
                      key={seller.name}
                      onClick={() => setSellerFilter(seller.name)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        sellerFilter === seller.name
                          ? 'bg-green-600 text-white'
                          : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                      }`}
                      title={`Loja: ${seller.lojaName}`}
                    >
                      {seller.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Language Filter */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
                <Globe size={14} />
                IDIOMA
              </label>
              <div className="flex flex-wrap gap-2">
                {LANGUAGE_FILTER_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setLanguageFilter(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      languageFilter === opt.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                    }`}
                  >
                    {opt.flag} {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Pills */}
        {activeFiltersCount > 0 && !showFilters && (
          <div className="flex flex-wrap gap-2 mt-3">
            {cityFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] rounded-full text-xs font-medium">
                <Building2 size={12} />
                {cityFilter}
                <button onClick={() => setCityFilter('all')} className="hover:text-red-500">
                  <X size={12} />
                </button>
              </span>
            )}
            {stateFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] rounded-full text-xs font-medium">
                <MapPinned size={12} />
                {stateFilter}
                <button onClick={() => setStateFilter('all')} className="hover:text-red-500">
                  <X size={12} />
                </button>
              </span>
            )}
            {sellerFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-600 rounded-full text-xs font-medium">
                <User size={12} />
                {sellerFilter}
                <button onClick={() => setSellerFilter('all')} className="hover:text-red-500">
                  <X size={12} />
                </button>
              </span>
            )}
            {languageFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-600 rounded-full text-xs font-medium">
                <Globe size={12} />
                {LANGUAGE_FLAGS[languageFilter]} {LANGUAGE_LABELS[languageFilter]}
                <button onClick={() => setLanguageFilter('all')} className="hover:text-red-500">
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Counter */}
      <p data-testid="lojas-counter" className="text-sm font-medium text-[var(--text-secondary)]">
        {filteredLojas.length} revenda{filteredLojas.length !== 1 ? 's' : ''} cadastrada{filteredLojas.length !== 1 ? 's' : ''}
      </p>

      {/* Store List */}
      <div className="flex flex-col gap-3 pb-20">
        {filteredLojas.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            <StoreIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma loja encontrada</p>
          </div>
        ) : (
          filteredLojas.map((loja, index) => {
            // Get store number - use existing numero or calculate from list position
            const storeNumber = loja.numero || (lojas.findIndex(l => l.id === loja.id) + 1);
            
            return (
            <div
              key={loja.id}
              data-testid={`loja-card-${loja.id}`}
              className="bg-[var(--bg-card)] rounded-[12px] border border-[var(--border-color)] border-l-[4px] border-l-[var(--accent-primary)] shadow-sm p-4 flex flex-col gap-2 theme-transition relative"
            >
              {/* Store Number Badge */}
              <div className="absolute -top-2 -left-2 w-8 h-8 bg-[var(--accent-primary)] rounded-full flex items-center justify-center shadow-lg border-2 border-[var(--bg-card)]">
                <span className="text-white text-xs font-bold">{storeNumber}</span>
              </div>

              {/* Header */}
              <div className="flex items-start justify-between pl-6">
                <h3 className="text-base font-semibold text-[var(--text-primary)] leading-tight">
                  {loja.name}
                </h3>
                <span 
                  data-testid={`gps-badge-${loja.id}`}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    loja.lat && loja.lng 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-[var(--bg-chip)] text-[var(--text-muted)]'
                  }`}
                >
                  {loja.lat && loja.lng ? 'GPS' : 'Sem GPS'}
                </span>
              </div>

              {/* Address */}
              <p className="text-sm text-[var(--text-secondary)]">
                {loja.address}, {loja.city}, {loja.state} {loja.zip}
              </p>

              {/* Info Chips */}
              <div className="flex flex-wrap gap-2 mt-1">
                {loja.manager && (
                  <span className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] bg-[var(--bg-chip)] px-2 py-1 rounded-full">
                    <User size={12} /> {loja.manager}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] bg-[var(--bg-chip)] px-2 py-1 rounded-full">
                  <Calendar size={12} /> {formatDate(loja.visit)}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] bg-[var(--bg-chip)] px-2 py-1 rounded-full">
                  <Users size={12} /> {loja.sellers?.length || 0} vendedor{(loja.sellers?.length || 0) !== 1 ? 'es' : ''}
                </span>
                {loja.language && (
                  <span 
                    data-testid={`language-badge-${loja.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] bg-[var(--bg-chip)] px-2 py-1 rounded-full"
                  >
                    <span>{LANGUAGE_FLAGS[loja.language] || '🌐'}</span>
                    {LANGUAGE_LABELS[loja.language] || loja.language}
                  </span>
                )}
                {loja.visitHistory && loja.visitHistory.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-500/20 px-2 py-1 rounded-full">
                    <History size={12} /> {loja.visitHistory.length} visita{loja.visitHistory.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Sellers */}
              {loja.sellers && loja.sellers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {loja.sellers.map((seller) => (
                    <a
                      key={seller.id || `${seller.name}_${seller.phone}`}
                      href={`tel:${seller.phone.replace(/\D/g, '')}`}
                      className="text-xs text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 px-2 py-1 rounded-full hover:bg-[var(--accent-primary)]/20 transition-colors"
                    >
                      {seller.name} · {seller.phone}
                    </a>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--border-color)]">
                <button
                  data-testid={`btn-edit-${loja.id}`}
                  onClick={() => onEdit(loja)}
                  className="flex-1 p-2 rounded-md text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 active:bg-[var(--accent-primary)]/20 flex items-center justify-center gap-1 text-xs transition-colors"
                >
                  <Pencil size={14} /> Editar
                </button>
                <button
                  data-testid={`btn-map-${loja.id}`}
                  onClick={() => onViewOnMap(loja)}
                  disabled={!loja.lat || !loja.lng}
                  className="flex-1 p-2 rounded-md text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 active:bg-[var(--accent-primary)]/20 flex items-center justify-center gap-1 text-xs transition-colors disabled:opacity-40"
                >
                  <MapPin size={14} /> Mapa
                </button>
                <button
                  data-testid={`btn-navigate-${loja.id}`}
                  onClick={() => setNavigationLoja(loja)}
                  disabled={!loja.lat || !loja.lng}
                  className="flex-1 p-2 rounded-md text-green-600 bg-green-500/10 active:bg-green-500/20 flex items-center justify-center gap-1 text-xs transition-colors disabled:opacity-40"
                >
                  <Route size={14} /> Ir
                </button>
                <button
                  data-testid={`btn-gps-${loja.id}`}
                  onClick={() => handleGeocode(loja)}
                  className="p-2 rounded-md text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 active:bg-[var(--accent-primary)]/20 flex items-center justify-center transition-colors"
                >
                  <Navigation size={14} />
                </button>
                <button
                  data-testid={`btn-pin-${loja.id}`}
                  onClick={() => onPinMode(loja)}
                  className="p-2 rounded-md text-purple-500 bg-purple-500/10 active:bg-purple-500/20 flex items-center justify-center transition-colors"
                >
                  <MapPin size={14} />
                </button>
                <button
                  data-testid={`btn-delete-${loja.id}`}
                  onClick={() => handleDelete(loja)}
                  className="p-2 rounded-md text-red-500 bg-red-500/10 active:bg-red-500/20 flex items-center justify-center transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Navigation Modal */}
      {navigationLoja && (
        <NavigationModal 
          loja={navigationLoja} 
          onClose={() => setNavigationLoja(null)} 
        />
      )}

      {/* FAB New Store */}
      <button
        data-testid="btn-nova-loja"
        onClick={() => onEdit(null)}
        className="fixed bottom-20 left-4 right-4 h-12 bg-[var(--accent-primary)] text-white rounded-lg shadow-lg flex items-center justify-center gap-2 active:opacity-90 z-40 transition-transform active:scale-[0.98] font-medium"
      >
        <Plus size={20} /> Nova Loja
      </button>
    </div>
  );
}
